import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import { fadeTransition, FadeVariant, fadeVariants } from '@pkg/motion';
import {
    designColor,
    floorGameDuration as duration,
    floorGameEasing as easing,
    styledInteraction,
    styledMedia,
    styledReset,
    styledTypography,
} from '@pkg/primitives';

import { tokens } from './CurrencySelector.tokens';

interface StyledCurrencySelectorProps {
    $baseHeight?: number;
    $compact?: boolean;
    $glass?: boolean;
}

interface MenuPopoverProps {
    // Since `MenuWrapper` is rendered through a `Portal`,
    // we need to repeat some props, since they cannot
    // be inherited by `font-size` or custom property.
    $baseHeight?: number;
    $compact?: boolean;
}

export const StyledCurrencySelector = styled.div<StyledCurrencySelectorProps>`
    ${styledReset.borderBox};
    --currency-selector-color: rgb(255, 255, 255, 1);

    --currency-selector-bg-color: ${tokens.action.bgColor.base};
    --currency-selector-bg-color-hover: ${tokens.action.bgColor.hover};

    --currency-selector-border-content: '';
    --currency-selector-border-color: ${tokens.action.borderColor.base};

    --currency-selector-min-width: ${({ $compact }) => ($compact ? undefined : `${tokens.action.minWidth}em`)};

    font-size: ${({ $baseHeight = tokens.baseHeight }) => $baseHeight}px;

    ${({ $glass }) =>
        $glass &&
        css`
            --currency-selector-bg-color: rgb(255, 255, 255, 0.1);
            --currency-selector-bg-color-hover: rgb(255, 255, 255, 0.14);
            --currency-selector-border-content: none;
            --currency-selector-backdrop-filter: blur(6px);
        `};
`;

export const Action = styled.button.attrs({ type: 'button' })`
    ${styledReset.button};

    position: relative;
    display: flex;
    align-items: center;
    gap: ${tokens.action.gap}em;
    padding: ${tokens.action.padding}em;
    min-width: var(--currency-selector-min-width);
    width: 100%;
    font-size: inherit;
    text-align: left;
    border-radius: 999px;
    background-color: var(--currency-selector-bg-color);
    backdrop-filter: var(--currency-selector-backdrop-filter);
    outline: 2px solid transparent !important;
    outline-offset: 2px;
    transition-property: opacity, background-color, outline-color;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};

    &::before {
        ${styledInteraction.interactionDisable};
        content: var(--currency-selector-border-content);
        display: block;
        position: absolute;
        inset: 0;
        border: 1px solid var(--currency-selector-border-color);
        border-radius: 999px;
        transition: border-color ${duration.normal}ms ${easing.cubic};
    }

    &[aria-controls] {
        --currency-selector-bg-color: ${designColor.brandPurple};
        --currency-selector-bg-color-hover: ${designColor.brandPurpleLight};
        --currency-selector-border-color: ${designColor.brandPurple};

        &:hover:not(:active) {
            --currency-selector-border-color: ${designColor.brandPurpleLight};
        }
    }

    &[disabled] {
        ${styledInteraction.interactionDisable};
        opacity: 0.8;
    }

    &:focus-visible {
        outline-color: ${designColor.brandPurple} !important;
    }

    &:hover:not(:active) {
        --currency-selector-border-color: ${tokens.action.borderColor.hover};
        background-color: var(--currency-selector-bg-color-hover);
    }
`;

export const Symbol = styled.div`
    display: grid;
    place-items: center;
    place-content: center;
    width: ${tokens.symbol.size}em;
    height: ${tokens.symbol.size}em;
    border-radius: 999px;
    background-color: rgb(255, 255, 255, 1);
`;

export const IconWrapper = styled.div`
    ${styledMedia.iconWrapper};
    width: ${tokens.symbol.iconSize}em;
    height: ${tokens.symbol.iconSize}em;
    color: ${designColor.eerie};
`;

export const Details = styled.div`
    display: grid;
    gap: ${tokens.details.gap}em;
    flex: 1 1 auto;
`;

export const Label = styled.p`
    ${styledTypography.allianceBase};
    font-size: ${tokens.label.fontSize}em;
    font-weight: 500;
    letter-spacing: -0.02em;
    white-space: nowrap;
    color: var(--currency-selector-color);
`;

export const Name = styled.p`
    ${styledTypography.allianceBase};
    font-size: ${tokens.name.fontSize}em;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--currency-selector-color);
`;

export const ArrowWrapper = styled.div`
    ${styledMedia.iconWrapper};
    margin-left: ${tokens.action.padding}em;
    margin-right: ${tokens.action.padding}em;
    width: ${tokens.arrow.size}em;
    height: ${tokens.arrow.size}em;
`;

export const MenuPopover = styled(motion.div).attrs({
    variants: fadeVariants,
    initial: FadeVariant.Hidden,
    animate: FadeVariant.Show,
    exit: FadeVariant.Hidden,
    transition: fadeTransition,
})<MenuPopoverProps>`
    // TODO: Pull this z-index from a source of truth for the TopNav.
    z-index: 10;
    min-width: ${({ $compact }) => ($compact ? undefined : `${tokens.action.minWidth}em`)};
    width: max-content;
    font-size: ${({ $baseHeight = tokens.baseHeight }) => $baseHeight}px;
`;
